import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import BAModeNav from '@/components/BAModeNav';
import { toast, Toaster } from 'sonner';
import { Clapperboard, Upload, Download, Lock, Play, Pause, RotateCcw, Columns, Rows, Loader2, Save } from 'lucide-react';
import { urlToBitmap, drawCoverEl, pill, drawLogoWatermark, knockoutWhite } from '@/lib/imageUtils';

const PRESETS = [
  { key: 'square', label: 'Square 1:1', hint: 'Feed', w: 1080, h: 1080 },
  { key: 'portrait', label: 'Portrait 4:5', hint: 'Feed', w: 1080, h: 1350 },
  { key: 'story', label: 'Story 9:16', hint: 'Reels', w: 1080, h: 1920 },
  { key: 'landscape', label: 'Landscape 16:9', hint: 'Facebook', w: 1080, h: 608 },
];
const MAX_SECONDS = 120;

function pickMime() {
  const opts = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  if (typeof MediaRecorder === 'undefined') return null;
  return opts.find((m) => MediaRecorder.isTypeSupported(m)) || null;
}

export default function BeforeAfterVideoPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const canvasRef = useRef(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const rafRef = useRef(null);
  const recRef = useRef(null);
  const playingRef = useRef(false);
  const frozenRef = useRef({ before: null, after: null });

  const [layout, setLayout] = useState('side'); // side | stack
  const [presetKey, setPresetKey] = useState('square');
  const [brand, setBrand] = useState('#FF5A1F');

  // Default the media accent to the user's saved brand colour.
  useEffect(() => { if (user?.brand_color) setBrand(user.brand_color); }, [user?.brand_color]);

  const [showLabels, setShowLabels] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [logoPos, setLogoPos] = useState('br');
  const [logoSize, setLogoSize] = useState('m');
  const [logoBmp, setLogoBmp] = useState(null);
  const [logoOriginal, setLogoOriginal] = useState(null);
  const [logoTransparent, setLogoTransparent] = useState(false);
  const [logoThreshold, setLogoThreshold] = useState(238);

  const [showIntro, setShowIntro] = useState(true);
  const [showOutro, setShowOutro] = useState(true);
  const [introTitle, setIntroTitle] = useState('Before & After');
  const [previewCard, setPreviewCard] = useState('live'); // live | intro | outro
  const forceCardRef = useRef(null);
  const [savedBlob, setSavedBlob] = useState(null);
  const [savingGallery, setSavingGallery] = useState(false);

  const [beforeReady, setBeforeReady] = useState(false);
  const [afterReady, setAfterReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const preset = PRESETS.find((p) => p.key === presetKey);

  useEffect(() => {
    api.get('/me/logo').then((r) => {
      const raw = r.data?.original || r.data?.data_url;
      if (!raw) return;
      setLogoOriginal(raw); setLogoTransparent(!!r.data?.transparent); setLogoThreshold(r.data?.threshold || 238);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!logoOriginal) { setLogoBmp(null); return; }
    (async () => {
      try {
        const src = logoTransparent ? await knockoutWhite(logoOriginal, logoThreshold) : logoOriginal;
        const bmp = await urlToBitmap(src);
        if (!cancelled) setLogoBmp(bmp);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [logoOriginal, logoTransparent, logoThreshold]);

  const cells = layout === 'side'
    ? [{ x: 0, y: 0, w: 0.5, h: 1 }, { x: 0.5, y: 0, w: 0.5, h: 1 }]
    : [{ x: 0, y: 0, w: 1, h: 0.5 }, { x: 0, y: 0.5, w: 1, h: 0.5 }];

  // Persistent draw loop — renders current video frames + overlays to the preview canvas.
  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const { w, h } = preset;
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    const scale = w / 1080;
    const bv = beforeRef.current, av = afterRef.current;
    const vids = [bv, av];
    const labels = ['BEFORE', 'AFTER'];

    const fitFont = (text, maxW, startPx, weight = '900') => {
      let size = startPx;
      ctx.font = `${weight} ${size}px Arial`;
      while (ctx.measureText(text).width > maxW && size > 14) { size -= 2; ctx.font = `${weight} ${size}px Arial`; }
      return size;
    };
    const drawIntro = () => {
      ctx.fillStyle = brand; ctx.fillRect(0, 0, w, h);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (logoBmp) {
        const lh = Math.min(240 * scale, h * 0.22); const lw = lh * (logoBmp.width / logoBmp.height);
        ctx.drawImage(logoBmp, (w - lw) / 2, h * 0.30 - lh, lw, lh);
      }
      const title = (introTitle || 'Before & After').toUpperCase();
      const ts = fitFont(title, w * 0.86, Math.round(100 * scale));
      ctx.fillStyle = '#ffffff'; ctx.font = `900 ${ts}px Arial`;
      ctx.fillText(title, w / 2, h * 0.5);
      const sub = (user?.business_name || '').toUpperCase();
      if (sub) { ctx.font = `700 ${Math.round(34 * scale)}px Arial`; ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.fillText(sub, w / 2, h * 0.5 + ts * 0.75); }
    };
    const drawOutro = () => {
      ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = brand; ctx.fillRect(0, 0, w, Math.round(10 * scale)); ctx.fillRect(0, h - Math.round(10 * scale), w, Math.round(10 * scale));
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      if (logoBmp) { const lh = Math.min(200 * scale, h * 0.2); const lw = lh * (logoBmp.width / logoBmp.height); ctx.drawImage(logoBmp, (w - lw) / 2, h * 0.24 - lh / 2, lw, lh); }
      const name = (user?.business_name || 'Get in touch').toUpperCase();
      const ns = fitFont(name, w * 0.86, Math.round(74 * scale));
      ctx.fillStyle = '#ffffff'; ctx.font = `900 ${ns}px Arial`; ctx.fillText(name, w / 2, h * 0.46);
      ctx.font = `700 ${Math.round(34 * scale)}px Arial`; ctx.fillStyle = 'rgba(255,255,255,0.85)';
      let ly = h * 0.46 + ns * 0.85;
      [user?.business_phone, user?.business_email].filter(Boolean).forEach((t) => { ctx.fillText(t, w / 2, ly); ly += 50 * scale; });
      const cta = 'GET A FREE QUOTE'; ctx.font = `900 ${Math.round(38 * scale)}px Arial`;
      const tw = ctx.measureText(cta).width; const pw = tw + 80 * scale, ph = 84 * scale; const px = (w - pw) / 2, py = h * 0.8;
      ctx.fillStyle = brand; ctx.fillRect(px, py, pw, ph);
      ctx.fillStyle = '#ffffff'; ctx.fillText(cta, w / 2, py + ph / 2 + 2 * scale);
    };

    const draw = () => {
      const card = forceCardRef.current || previewCard;
      if (card === 'intro') { drawIntro(); rafRef.current = requestAnimationFrame(draw); return; }
      if (card === 'outro') { drawOutro(); rafRef.current = requestAnimationFrame(draw); return; }
      ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, w, h);
      cells.forEach((c, i) => {
        const rx = Math.round(c.x * w), ry = Math.round(c.y * h), rw = Math.round(c.w * w), rh = Math.round(c.h * h);
        const v = vids[i];
        const frozen = frozenRef.current[i === 0 ? 'before' : 'after'];
        const src = (v && v.ended && frozen) ? frozen : v;
        const sw = src ? (src.videoWidth || src.width) : 0;
        const sh = src ? (src.videoHeight || src.height) : 0;
        if (src && sw) drawCoverEl(ctx, src, sw, sh, rx, ry, rw, rh);
        else {
          ctx.fillStyle = '#1e293b'; ctx.fillRect(rx, ry, rw, rh);
          ctx.fillStyle = '#64748b'; ctx.font = `700 ${Math.round(30 * scale)}px Arial`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(i === 0 ? 'Before video' : 'After video', rx + rw / 2, ry + rh / 2);
        }
        if (showLabels) pill(ctx, labels[i], rx + Math.round(24 * scale), ry + Math.round(24 * scale), scale, brand);
      });
      ctx.fillStyle = brand;
      if (layout === 'side') ctx.fillRect(Math.round(w / 2 - 2 * scale), 0, Math.round(4 * scale), h);
      else ctx.fillRect(0, Math.round(h / 2 - 2 * scale), w, Math.round(4 * scale));
      if (showLogo && logoBmp) drawLogoWatermark(ctx, logoBmp, { areaW: w, areaH: h, pos: logoPos, size: logoSize, scale, transparent: logoTransparent });
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [layout, presetKey, showLabels, showLogo, logoPos, logoSize, logoBmp, logoTransparent, brand, preset, cells, introTitle, previewCard, user]);

  const onPick = (which) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const ref = which === 'before' ? beforeRef : afterRef;
    const setReady = which === 'before' ? setBeforeReady : setAfterReady;
    setReady(false);
    frozenRef.current[which] = null;
    const v = ref.current;
    v.src = url; v.muted = true; v.playsInline = true; v.load();
    v.onloadeddata = () => { setReady(true); v.currentTime = 0; };
    // Freeze the final frame to a static canvas so an ended clip stops decoding
    // (prevents lag/glitch while the longer clip keeps playing).
    v.onended = () => {
      if (!v.videoWidth) return;
      const c = document.createElement('canvas');
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext('2d').drawImage(v, 0, 0);
      frozenRef.current[which] = c;
    };
    e.target.value = '';
  };

  const bothReady = beforeReady && afterReady;

  const playBoth = async () => {
    const bv = beforeRef.current, av = afterRef.current;
    if (!bv || !av) return;
    playingRef.current = true; setIsPlaying(true);
    try { await Promise.all([bv.play(), av.play()]); } catch { /* autoplay guard */ }
  };
  const pauseBoth = () => {
    playingRef.current = false; setIsPlaying(false);
    beforeRef.current?.pause(); afterRef.current?.pause();
  };
  const restart = () => {
    const bv = beforeRef.current, av = afterRef.current;
    frozenRef.current = { before: null, after: null };
    if (bv) bv.currentTime = 0; if (av) av.currentTime = 0;
  };
  const togglePlay = () => { if (isPlaying) pauseBoth(); else playBoth(); };

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const exportVideo = async () => {
    if (!bothReady) { toast.error('Upload both a Before and an After video first'); return; }
    const mime = pickMime();
    const cv = canvasRef.current;
    if (!mime || !cv.captureStream) { toast.error('Your browser can’t export video here — try Chrome on desktop.'); return; }
    const bv = beforeRef.current, av = afterRef.current;
    setExporting(true); setProgress(0); setSavedBlob(null);
    try {
      const stream = cv.captureStream(30);
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 12_000_000 });
      recRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      const done = new Promise((resolve) => { rec.onstop = () => resolve(); });
      rec.start(200);

      if (showIntro) { forceCardRef.current = 'intro'; await wait(1800); }

      forceCardRef.current = 'live';
      restart();
      bv.muted = true; av.muted = true;
      playingRef.current = true; setIsPlaying(true);
      await Promise.all([bv.play(), av.play()]);
      const dur = Math.min(MAX_SECONDS, Math.max(bv.duration || 0, av.duration || 0) || 0);
      const start = performance.now();
      await new Promise((resolve) => {
        const tick = () => {
          const elapsed = (performance.now() - start) / 1000;
          if (dur) setProgress(Math.min(95, Math.round((elapsed / dur) * 100)));
          const ended = (bv.ended || elapsed >= (bv.duration || 0)) && (av.ended || elapsed >= (av.duration || 0));
          if (ended || (dur && elapsed >= dur + 0.15) || elapsed >= MAX_SECONDS + 1) { resolve(); return; }
          setTimeout(tick, 120);
        };
        tick();
      });
      pauseBoth();

      if (showOutro) { forceCardRef.current = 'outro'; await wait(2200); }
      forceCardRef.current = null;

      if (rec.state !== 'inactive') rec.stop();
      await done;

      const blob = new Blob(chunks, { type: mime });
      setSavedBlob(blob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `before-after-video-${layout}-${presetKey}.webm`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setProgress(100);
      toast.success('Video exported — ready to post!');
    } catch (e) {
      toast.error('Export failed — try shorter clips or Chrome on desktop.');
    } finally {
      forceCardRef.current = null;
      setExporting(false);
      restart();
    }
  };

  const saveVideoToGallery = async () => {
    if (!savedBlob) return;
    setSavingGallery(true);
    try {
      const fd = new FormData();
      fd.append('file', savedBlob, `walkthrough-${Date.now()}.webm`);
      fd.append('caption', '');
      fd.append('kind', 'video');
      await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Video saved to your gallery');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to gallery');
    } finally { setSavingGallery(false); }
  };

  if (user && !isPro) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="bav-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Make side-by-side before &amp; after walkthrough videos of your jobs. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="bav-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title="Before & After Video" noindex />
      <Toaster richColors position="top-right" />
      <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Marketing</p>
      <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1 mb-4 flex items-center gap-3"><Clapperboard className="text-brand" size={36} /> Before &amp; After Video</h1>
      <BAModeNav />

      {/* hidden source videos */}
      <video ref={beforeRef} className="hidden" playsInline muted preload="auto" />
      <video ref={afterRef} className="hidden" playsInline muted preload="auto" />

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="tm-card p-3 sm:p-4">
          <canvas ref={canvasRef} data-testid="bav-canvas" className="w-full h-auto border border-slate-800 bg-slate-900" style={{ maxHeight: '68vh', objectFit: 'contain' }} />
          <div className="mt-3 flex items-center gap-2">
            <button onClick={togglePlay} disabled={!bothReady || exporting} data-testid="bav-play" className="tm-btn">
              {isPlaying ? <Pause size={16} /> : <Play size={16} />} {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button onClick={restart} disabled={!bothReady || exporting} data-testid="bav-restart" className="tm-btn tm-btn-ghost"><RotateCcw size={16} /> Restart</button>
            <div className="flex items-center gap-1 ml-auto" data-testid="bav-preview-switch">
              {[['live', 'Video'], ['intro', 'Intro'], ['outro', 'Outro']].map(([v, l]) => (
                <button key={v} onClick={() => setPreviewCard(v)} data-testid={`bav-preview-${v}`}
                  className={`px-2.5 py-1.5 border text-[10px] font-mono uppercase ${previewCard === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="tm-card p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <label className="border border-dashed border-slate-200 p-3 text-center cursor-pointer hover:bg-slate-50" data-testid="bav-before-label">
              <input type="file" accept="video/*" className="hidden" onChange={onPick('before')} data-testid="bav-before-input" />
              <Upload size={18} className="mx-auto text-brand" />
              <span className="block font-display font-bold text-sm mt-1">Before</span>
              <span className="block text-[10px] uppercase text-slate-500 font-semibold">{beforeReady ? 'Loaded ✓' : 'Tap to upload'}</span>
            </label>
            <label className="border border-dashed border-slate-200 p-3 text-center cursor-pointer hover:bg-slate-50" data-testid="bav-after-label">
              <input type="file" accept="video/*" className="hidden" onChange={onPick('after')} data-testid="bav-after-input" />
              <Upload size={18} className="mx-auto text-brand" />
              <span className="block font-display font-bold text-sm mt-1">After</span>
              <span className="block text-[10px] uppercase text-slate-500 font-semibold">{afterReady ? 'Loaded ✓' : 'Tap to upload'}</span>
            </label>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Layout</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setLayout('side')} data-testid="bav-layout-side" className={`border py-2 flex items-center justify-center gap-2 text-xs font-mono uppercase ${layout === 'side' ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}><Columns size={15} /> Side</button>
              <button onClick={() => setLayout('stack')} data-testid="bav-layout-stack" className={`border py-2 flex items-center justify-center gap-2 text-xs font-mono uppercase ${layout === 'stack' ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}><Rows size={15} /> Stacked</button>
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Format</p>
            <div className="grid grid-cols-2 gap-1">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => setPresetKey(p.key)} data-testid={`bav-preset-${p.key}`}
                  className={`border px-2 py-1.5 text-left ${presetKey === p.key ? 'border-slate-200 bg-slate-50' : 'border-slate-300'}`}>
                  <span className="block text-xs font-bold">{p.label}</span>
                  <span className="block text-[10px] text-slate-500">{p.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowLabels(!showLabels)} data-testid="bav-toggle-labels" className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-display font-bold uppercase ${showLabels ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${showLabels ? 'bg-brand' : 'bg-slate-300'}`} /> Labels
            </button>
            <button onClick={() => setShowLogo(!showLogo)} data-testid="bav-toggle-logo" className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-display font-bold uppercase ${showLogo ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
              <span className={`w-2.5 h-2.5 rounded-full ${showLogo ? 'bg-brand' : 'bg-slate-300'}`} /> Logo
            </button>
          </div>

          {showLogo && logoBmp && (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-1">
                {[['tl', 'TL'], ['tr', 'TR'], ['bl', 'BL'], ['br', 'BR']].map(([v, l]) => (
                  <button key={v} onClick={() => setLogoPos(v)} data-testid={`bav-logopos-${v}`} className={`border py-1.5 text-[10px] font-mono ${logoPos === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-1">
                {[['s', 'Small'], ['m', 'Medium'], ['l', 'Large']].map(([v, l]) => (
                  <button key={v} onClick={() => setLogoSize(v)} data-testid={`bav-logosize-${v}`} className={`border py-1.5 text-[10px] font-mono uppercase ${logoSize === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none border border-slate-200 px-3 py-2">
                <input data-testid="bav-logo-transparent" type="checkbox" checked={logoTransparent} onChange={(e) => setLogoTransparent(e.target.checked)} className="w-4 h-4 accent-brand" />
                <span className="text-[11px] uppercase tracking-wider text-slate-700 font-semibold">Transparent logo</span>
              </label>
            </div>
          )}
          {showLogo && !logoBmp && (
            <p className="text-[11px] text-slate-400">No logo found — add one under <a href="/settings?tab=branding" className="text-brand underline">Settings → Branding</a>.</p>
          )}

          <div className="border border-slate-200 p-3 space-y-2">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Branded cards</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowIntro(!showIntro)} data-testid="bav-toggle-intro" className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-display font-bold uppercase ${showIntro ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${showIntro ? 'bg-brand' : 'bg-slate-300'}`} /> Intro card
              </button>
              <button onClick={() => setShowOutro(!showOutro)} data-testid="bav-toggle-outro" className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-display font-bold uppercase ${showOutro ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${showOutro ? 'bg-brand' : 'bg-slate-300'}`} /> End card
              </button>
            </div>
            {showIntro && (
              <input value={introTitle} onChange={(e) => setIntroTitle(e.target.value)} data-testid="bav-intro-title" placeholder="Intro title (e.g. Kitchen Transformation)" className="tm-input" />
            )}
            <p className="text-[11px] text-slate-500">The end card uses your business name, phone &amp; email from <a href="/settings?tab=business" className="text-brand underline">Settings</a>. Use the Intro/Outro preview buttons above to see them.</p>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <button onClick={exportVideo} disabled={!bothReady || exporting} data-testid="bav-export" className="tm-btn w-full">
              {exporting ? <><Loader2 size={16} className="animate-spin" /> Exporting… {progress}%</> : <><Download size={16} /> Export video (.webm)</>}
            </button>
            {savedBlob && (
              <button onClick={saveVideoToGallery} disabled={savingGallery} data-testid="bav-save-gallery" className="tm-btn tm-btn-ghost w-full mt-2">
                {savingGallery ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save video to gallery</>}
              </button>
            )}
            <p className="text-[11px] text-slate-400 mt-2">Exported in your browser (muted, up to {MAX_SECONDS}s). For best results use short clips and Chrome on desktop.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
